import type {
  Administration,
  BloodUnit,
  ChartRemark,
  NewBloodUnit,
  NewTransfusionRequest,
  Screen,
  TransfusionRequest,
  UnitStatus
} from '../pages/Doctor/Encounter/Manage Orders/bloodFluids/catalog'
import { SCREEN_TESTS, blankScreen } from '../pages/Doctor/Encounter/Manage Orders/bloodFluids/catalog'
import AXIOS_INSTANCE from './axiosInstance'

const jsonApi = { headers: { 'Content-Type': 'application/vnd.api+json' } }

type Resource = {
  id: string
  attributes: Record<string, unknown>
}

type Collection = { data?: Resource[] }
type Single = { data?: Resource }

const day = (value: unknown) => String(value || '').slice(0, 10)

const mapScreen = (raw: unknown): Screen => {
  const screen = blankScreen()
  if (!raw || typeof raw !== 'object') return screen
  const source = raw as Record<string, string>
  SCREEN_TESTS.forEach(test => {
    if (source[test] === 'pending' || source[test] === 'non-reactive') screen[test] = source[test]
  })
  return screen
}

const mapAdministration = (raw: unknown): Administration | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const admin = raw as Record<string, unknown>
  if (!admin.id) return undefined
  return {
    endedAt: String(admin.ended_at || ''),
    id: String(admin.id),
    note: String(admin.note || ''),
    remark: String(admin.remark || 'No reaction') as ChartRemark,
    returnedToBank: Boolean(admin.returned_to_bank),
    startedAt: String(admin.started_at || ''),
    steps: Array.isArray(admin.steps) ? admin.steps.map(String) : []
  }
}

export const mapTransfusionRequest = (raw: Resource): TransfusionRequest => ({
  abo: raw.attributes.abo as TransfusionRequest['abo'],
  createdAt: String(raw.attributes.created_at || ''),
  encounterId: String(raw.attributes.encounter_id || ''),
  id: String(raw.id),
  indication: String(raw.attributes.indication || ''),
  note: String(raw.attributes.note || ''),
  patientName: String(raw.attributes.patient_name || ''),
  pcv: Number(raw.attributes.pcv || 0),
  product: raw.attributes.product as TransfusionRequest['product'],
  rh: raw.attributes.rh as TransfusionRequest['rh'],
  screen: mapScreen(raw.attributes.screen),
  units: Number(raw.attributes.units || 0)
})

export const mapBloodUnit = (raw: Resource): BloodUnit => ({
  abo: raw.attributes.abo as BloodUnit['abo'],
  administration: mapAdministration(raw.attributes.administration),
  collectionDate: day(raw.attributes.collection_date),
  crossmatch: (raw.attributes.crossmatch || undefined) as BloodUnit['crossmatch'],
  expiry: day(raw.attributes.expiry),
  id: String(raw.id),
  product: raw.attributes.product as BloodUnit['product'],
  rh: raw.attributes.rh as BloodUnit['rh'],
  serology: raw.attributes.serology as BloodUnit['serology'],
  status: raw.attributes.status as UnitStatus,
  transfusionRequestId: raw.attributes.transfusion_request_id
    ? String(raw.attributes.transfusion_request_id)
    : undefined,
  unitNumber: String(raw.attributes.unit_number || '')
})

export async function listTransfusionRequests(encounterId?: string) {
  const { data } = await AXIOS_INSTANCE.get<Collection>('/transfusion_requests', {
    params: {
      'page[size]': 500,
      ...(encounterId ? { encounter_id: encounterId } : {})
    }
  })
  return (data.data || []).map(mapTransfusionRequest)
}

export async function createTransfusionRequest(encounterId: string, input: NewTransfusionRequest) {
  const { data } = await AXIOS_INSTANCE.post<Single>(
    '/transfusion_requests',
    {
      data: {
        attributes: {
          abo: input.abo,
          indication: input.indication,
          note: input.note,
          pcv: input.pcv,
          product: input.product,
          rh: input.rh,
          screen: input.screen,
          units: input.units
        },
        relationships: {
          encounter: { data: { id: encounterId, type: 'encounter' } }
        },
        type: 'transfusion_request'
      }
    },
    jsonApi
  )
  return mapTransfusionRequest(data.data as Resource)
}

export async function listBloodUnits() {
  const { data } = await AXIOS_INSTANCE.get<Collection>('/blood_units', { params: { 'page[size]': 500 } })
  return (data.data || []).map(mapBloodUnit)
}

export async function createBloodUnit(input: NewBloodUnit) {
  const { data } = await AXIOS_INSTANCE.post<Single>(
    '/blood_units',
    {
      data: {
        attributes: {
          abo: input.abo,
          collection_date: input.collectionDate,
          expiry: input.expiry,
          product: input.product,
          rh: input.rh,
          serology: input.serology,
          unit_number: input.unitNumber
        },
        type: 'blood_unit'
      }
    },
    jsonApi
  )
  return mapBloodUnit(data.data as Resource)
}

export async function crossmatchBloodUnit(unitId: string, result: 'compatible' | 'incompatible', requestId: string) {
  const { data } = await AXIOS_INSTANCE.post<Single>(
    `/blood_units/${unitId}/crossmatch`,
    {
      data: {
        attributes: { result, transfusion_request_id: requestId },
        type: 'blood_unit'
      }
    },
    jsonApi
  )
  return mapBloodUnit(data.data as Resource)
}

export async function issueBloodUnit(unitId: string, requestId: string) {
  const { data } = await AXIOS_INSTANCE.post<Single>(
    `/blood_units/${unitId}/issue`,
    {
      data: {
        attributes: { transfusion_request_id: requestId },
        type: 'blood_unit'
      }
    },
    jsonApi
  )
  return mapBloodUnit(data.data as Resource)
}

export async function recordBloodAdministration(
  unitId: string,
  input: { requestId: string; startedAt: string; endedAt: string; remark: ChartRemark; note: string; steps: string[] }
) {
  const { data } = await AXIOS_INSTANCE.post<Single>(
    `/blood_units/${unitId}/administrations`,
    {
      data: {
        attributes: {
          ended_at: input.endedAt,
          note: input.note,
          remark: input.remark,
          started_at: input.startedAt,
          steps: input.steps,
          transfusion_request_id: input.requestId
        },
        type: 'transfusion_administration'
      }
    },
    jsonApi
  )
  return mapBloodUnit(data.data as Resource)
}
